import React, { useEffect, useState } from 'react';

import Flex from 'ui/Flex/Flex';
import GeneralModal from 'components/SharedComponents/Modals/GeneralModal';
import UserGroupMembers from './UserGroupMembers';
import UserGroupNameSetter from './UserGroupNameSetter';
import UserGroupSharedDashboards from './UserGroupSharedDashboards';
import { useGetGroupNameHelperText } from './hooks';
import useGetUserGroup from 'hooks/userGroups/useGetUserGroup';
import { useTranslation } from 'react-i18next';
import useUpdateUserGroup from 'hooks/userGroups/useUpdateUserGroup';

type EditUserGroupModalProps = {
    isOpen: boolean;
    onClose: () => void;
    existingGroupsNames: string[];
    userGroupId: number;
};

const EditUserGroupModal = ({
    isOpen,
    onClose,
    existingGroupsNames,
    userGroupId,
}: EditUserGroupModalProps): JSX.Element => {
    const { t } = useTranslation();
    const { data: userGroup, isLoading } = useGetUserGroup(userGroupId);

    const [membersEmails, setMembersEmails] = useState<string[]>([]);
    const [groupName, setGroupName] = useState('');
    const { mutate: updateUserGroup } = useUpdateUserGroup(
        userGroupId,
        groupName,
        membersEmails,
    );

    useEffect(() => {
        if (userGroup) {
            setGroupName(userGroup.name);
            setMembersEmails(userGroup.users.map((user) => user.email));
        }
    }, [userGroup]);

    const groupNameHelperText = useGetGroupNameHelperText(
        groupName,
        existingGroupsNames.filter((name) => name !== userGroup?.name),
    );

    if (isLoading || !userGroup) return <></>;

    const onGroupNameChange = (newGroupName: string) =>
        setGroupName(newGroupName);

    const containedButton = {
        text: t('common.confirm'),
        onClick: () => {
            updateUserGroup();
            onClose();
        },
        disabled: groupNameHelperText.length > 0,
        tooltip: groupNameHelperText,
        className: 'pendo-update-group-confirm',
    };
    const textButton = {
        text: t('common.cancel'),
        onClick: onClose,
    };
    return (
        <GeneralModal
            isOpen={isOpen}
            onClose={onClose}
            title={t('adminSpace.editUserGroup.modalTitle')}
            width="lg"
            textButton={textButton}
            containedButton={containedButton}
        >
            <Flex direction="column" sx={{ gap: 2 }}>
                <UserGroupNameSetter
                    name={groupName}
                    onNameChange={onGroupNameChange}
                    helperText={groupNameHelperText}
                />
                <UserGroupMembers
                    emails={membersEmails}
                    setEmails={setMembersEmails}
                />
                <UserGroupSharedDashboards
                    dashboards={userGroup.sharedDashboards}
                />
            </Flex>
        </GeneralModal>
    );
};

export default EditUserGroupModal;
